import { Injectable } from '@angular/core';
import { CanActivate, CanActivateChild, Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, first, map, switchMap } from 'rxjs/operators';
import { APP_ROUTES as ROUTES } from '../../app.routes';
import { CustomerAPI } from '../../data/mo-api/customer.api';
import { OnboardingAPI } from '../../data/mo-api/onboarding.api';
import { AppFacade } from '../../facades/app.facade';
import { CustomerFacade } from '../../facades/customer.facade';
import { OnboardingFacade } from '../../facades/onboarding.facade';
import { IAppSession } from '../models/app.model';
import { ICustomer } from '../models/customer.model';
import { IOnboardingSession } from '../models/onboarding.model';
import { HandleTokenService } from '../services/handle-token.service';

@Injectable({
  providedIn: 'root',
})
export class OnboardingGuard implements CanActivate, CanActivateChild {
  public session$: Observable<IAppSession>;

  constructor(
    private _route: Router,
    private _appFacade: AppFacade,
    private _onboardingFacade: OnboardingFacade,
    private _onboardingAPI: OnboardingAPI,
    private _handleTokenService: HandleTokenService,
    private _customerAPI: CustomerAPI,
    private _customerFacade: CustomerFacade
  ) {}

  canActivate(): Observable<boolean | UrlTree> | UrlTree {
    return this._handleSessionStore();
  }

  canActivateChild(): Observable<boolean | UrlTree> | UrlTree {
    return this._handleSessionStore();
  }

  private _handleSessionStore(): Observable<boolean | UrlTree> | UrlTree {
    this.session$ = this._appFacade.session$;
    return this.session$.pipe(
      first(),
      switchMap((session) => {
        return !!session?.token ? of(true) : this._handleSessionToken();
      })
    );
  }

  private _handleCustomer(): Observable<UrlTree | boolean> {
    this.session$ = this._appFacade.session$;
    return this.session$.pipe(
      first(),
      switchMap((isCustomer) => {
        if (!!isCustomer) {
          return this._customerAPI.requestCustomerInformation().pipe(
            map((customer: ICustomer) => {
              this._customerFacade.setCustomerInformation(customer);
              return true;
            }),
            catchError((_) => of(this._closeSession()))
          );
        }
      })
    );
  }

  private _handleSessionToken(): Observable<UrlTree | boolean> {
    const sessionToken = this._handleTokenService.getAccessToken();
    return !!sessionToken ? this._handleCustomer() : of(this._closeSession());
  }

  private _closeSession(): UrlTree {
    const route: string = ROUTES.AUTH;
    this._appFacade.closeSession();
    return this._route.createUrlTree([route]);
  }
}
